import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Injectable, Pipe, PipeTransform } from '@angular/core';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { CalendarButtons, CalendarDaysOfWeek, CalendarItem, CalendarItemType, CalendarTitle, CalendarViews, GroupedCalendarItems } from 'kfbs-nglib';
import { Observable, of, Subject, Subscription, combineLatest as cl, timer } from 'rxjs';
import { combineLatest, map, shareReplay, switchMap, takeUntil, tap } from 'rxjs/operators';
import { environment } from '../../environments/environment';
import { DisplayDetailsModalComponent } from '../core/modals/display-details/display-details.component';
import { AuthenticationService } from '../core/services/authentication.service';
import { LogCategory, LoggerService, LogService, LogType } from '../core/services/logger.service';
import { UtilityService } from '../core/services/utility.service';
import { EnrollmentsService } from './enrollments.service';

@Injectable()
export class BiddingService {
    private httpOptions = {
        headers: new HttpHeaders({
            'Content-Type': 'application/json',
        })
    };
    private _auctions$: Observable<any> = null;
    private _auctionInfo$: Observable<any> = null;
    private _reload$ = new Subject<void>();             // used to trigger resubscribe/reload
    private _subNewUser: Subscription = null;            // new user subscription for spoof/unspoof events    
    private _auctionActive$: Observable<any> = null;   
    private _auctionBids$: Observable<any> = null;
    private _bidHistory$: Observable<any> = null;
    private _bids$: Observable<any> = null;
    private _calendarBids$: Observable<BiddingCalendar> = null;

    private _apiUrlHistory = environment.apiUrl + '/bidding/history';

    constructor(
        private http: HttpClient,
        private logger: LoggerService,
        private auth: AuthenticationService,
        private enrollmentService: EnrollmentsService,
        private utility: UtilityService,
        private modalService: NgbModal,
    ) {
        this.log("constructor", null, LogCategory.Constructor);
        this._subNewUser = auth.loggedInUser$.subscribe((data) => {                                   
            this.update();
        })
    }

    private log(method: string = null, message = null, category: LogCategory = LogCategory.Default): void {
        method = (method) ? '.' + method + '()' : '';
        message = (message) ? message.toString() : '';
        this.logger.try(this.constructor.name + method + ': ' + message, LogType.Service, LogService.Bidding, category);
    }

    public update() {     
        this.log("update");
        this._reload$.next();
        this._auctions$ = null;
        this._auctionActive$ = null;
        // Use the property to cause it to try to fetch 
        var v = this._auctions$;
    }

    public get auctions$(): Observable<any> {

        if (!this._auctions$) {
            this._auctions$ = this.http.get<any>(environment.apiUrl + '/bidding/self/auctions').pipe(
                tap( (data) => this.log('auctions$', 'Fetched bidding auctions', LogCategory.SubscriptionData)),
                    takeUntil(this._reload$),
                    shareReplay(1)
                );
            }

        return this._auctions$;
    }

    public get bidHistory$(): Observable<any> {
        if (!this._bidHistory$) {
            this._bidHistory$ = this.http.get<any>(this._apiUrlHistory, this.httpOptions).pipe(
                tap((data) => this.log('bidHistory$', 'Fetched bidding auctions', LogCategory.SubscriptionData)),
                takeUntil(this._reload$),
                shareReplay(1)
            );
        }
        return this._bidHistory$
    }
   
    public get auctionActive$(): Observable<Auction[]> {
        if (!this._auctionActive$) {
            this._auctionActive$ = timer(0, 3000).pipe(
                combineLatest(this.auctions$),
                map(([a,auctions]) => {
                    var activeAuctions = [];
                    auctions.forEach((item) => {                        
                        var now = this.utility.getCurrentDate();                                                                     
                        var index = item.Rounds.findIndex(r => new Date(r.BiddingStart) <= now && new Date(r.SystemRecEnd) > now);
                        if (index > -1) {
                            var currentRound = item.Rounds[index];
                            const biddingStart = new Date(currentRound.BiddingStart);
                            const procStart = new Date(currentRound.ProcessingStart);
                            const studrecStart = new Date(currentRound.StudentRecStart);
                            const sysrecStart = new Date(currentRound.SystemRecStart);
                            const sysrecEnd = new Date(currentRound.SystemRecEnd);

                            var bidding = (biddingStart <= now) && (procStart > now);
                            var processing = (procStart <= now) && (studrecStart > now);
                            var studentrec = (studrecStart <= now) && (sysrecStart > now);
                            var sysrec = (sysrecStart <= now) && (sysrecEnd > now);

                            var active = bidding || processing || studentrec || sysrec;

                            activeAuctions.push(new Auction(item.AuctionId, bidding, processing, studentrec, sysrec, active, currentRound.Id))
                        } else {
                            var biddingstartIndex = item.Rounds.findIndex(r => new Date(r.BiddingStart) <= now);
                            var systemRecEndIndex = item.Rounds.findIndex(r => new Date(r.SystemRecEnd) > now);                          
                            if (biddingstartIndex > -1 && systemRecEndIndex > -1) //not in a current round but still in an active auction
                                activeAuctions.push(new Auction(0, false, false, false, false, true, 0));
                        }
                        
                    })
                    
                    return activeAuctions;
                }),
                shareReplay(1)
            )
                
        }
        return this._auctionActive$;
    }

    public get activeAuctions$(): Observable<Auction[]> {
        if (!this.auth.hasFeature('Bidding')) {
            var auctions: Auction[] = [new Auction(0, false, false, false, false, false, 0)];            
            return of(auctions).pipe(shareReplay(1));
        }
        return this.auctionActive$;
    }

    public refetchBids() {
        this._auctionBids$ = null;
    }

    public get bids$(): Observable<any> {

        if (!this._bids$) {
            this._bids$ = this.http.get<any>(environment.apiUrl + '/bidding/self/bids').pipe(
                tap((data) => this.log('auctions$', 'Fetched bids', LogCategory.SubscriptionData)),
                takeUntil(this._reload$),
                shareReplay(1)
            );
        }

        return this._bids$;
    }

    public get auctionBids$(): Observable<IBidPoints> {
        if (!this._auctionBids$) {
            this._auctionBids$ = timer(0, 3000).pipe(
                combineLatest(this.bids$),
                map(([a, auction]) => {
                    var now = this.utility.getCurrentDate();                                       
                    //Check each status in the timeline and assign Current and Next based on current date/time
                    if (auction && auction.Timeline) {
                        var auctionEnds = new Date(auction.AuctionEnds);
                        auction.Timeline.forEach((status, index) => {
                            status.Key = new Date(status.Key);
                            status.CurrentState = false;                          
                            if ((status.Key < now) && (index < auction.Timeline.length - 1)) {
                                var nextDate = new Date(auction.Timeline[index + 1].Key);
                                if (nextDate > now) {
                                    status.CurrentState = true;
                                    status.NextState = false;
                                    auction.Timeline[index + 1].NextState = true;
                                }
                            } else {
                                //if the last state has started, check if the auction has ended
                                if ((status.Key < now) && (index == auction.Timeline.length - 1)) {                                                                    
                                    if (auctionEnds > now) {                                        
                                        status.CurrentState = true;
                                        status.NextState = false;
                                    }                                    
                                }                                    
                            }
                        });
                        var auctionStart = new Date(auction.Timeline[0].Key);
                        var fortnight = new Date(now); //copy the date
                        fortnight.setDate(fortnight.getDate() + 14);
                        auction.hasStarted = true;
                        auction.isActive = true;

                        // We should be able to safely use the index as the timeline is strictly ordered.
                        auction.currentStateIndex = auction.Timeline.findIndex(s => s.CurrentState);

                        if ((auctionStart < fortnight) && (auctionStart > now)) { 
                            //starts in the next two weeks
                            auction.hasStarted = false;
                        } else {
                            if (auctionStart > now || auctionEnds < now ) { //has not started or has already ended
                                auction.isActive = false;
                                auction.hasStarted = false;
                            }                            
                        }                        
                    };
                    if (auction) {
                        auction.Bids.forEach((bid: IBid) => {
                            bid.isDeleted = false; // This is used for removing bids in the UI
                        });
                    }
                    
                    return auction;
                }),
                shareReplay(1)
            )
           
        }

        return this._auctionBids$;
    }

    public get auctionInfo$(): Observable<any> {
        if (!this._auctionInfo$) {
            this._auctionInfo$ = cl(this.auctions$, this.auctionBids$).pipe(
                switchMap(([auctions, points]) => {
                    // Bidding Message States ( B = bidding, P = processing, S = student reconciliation, A = admin reconciliation )
                    // ------------------------------------------------------------------------------------------------------------ 
                    //   State           (past)           Now               (future)            What to Show
                    //   1)                                |     (nothing)                          1     
                    //   2)                                |  BBBPPPSSSAAA     BBBPPPSSSAAA     A   2  m1 
                    //   3)                               BBBPPPSSSAAA     BBBPPPSSSAAA         B   3  m1 
                    //   4)                            BBBPPPSSSAAA     BBBPPPSSSAAA                4  m1 
                    //   5)                         BBBPPPSSSAAA     BBBPPPSSSAAA               C   5  m1  m2
                    //   6)                      BBBPPPSSSAAA     BBBPPPSSSAAA                  A   6  m1 
                    //   7)                  BBBPPPSSSAAA  |  BBBPPPSSSAAA                      A   7  m1 
                    //   8)              BBBPPPSSSAAA     BBBPPPSSSAAA                          B   3  m1 
                    //   9)           BBBPPPSSSAAA     BBBPPPSSSAAA                                 9     
                    //  10)        BBBPPPSSSAAA     BBBPPPSSSAAA                                C  10      m2
                    //  11)     BBBPPPSSSAAA     BBBPPPSSSAAA                                      11     
                    //  12) BBBPPPSSSAAA     BBBPPPSSSAAA  |                                       12
                    //   Banners
                    //   A) Bidding Starts Soon (on [BiddingStart])
                    //   B) Bidding Open until [BiddingEnd]
                    //   C) Reconciliation Open Until [StudRecEnd]. Reconcile your [conflict count] conflicts now. 
                    //
                    //   1) You currently have no upcoming bid rounds.
                    //   2) Bidding will start on [BiddingStart] for [Round name] of [GroupName].
                    //
                    //   States 3-7 can repeat if an auction has more than two rounds:
                    //   3) Bidding is currently open for [Round name] of [GroupName], and will close in [friendly duration) on [BiddingEnd]
                    //   4) Bidding has ended for [Round name] of [GroupName], and bids are currently being processed. You can reconcile your conflicts starting in [friendly duration] (from [StudentReconciliationStart] until [StudentReconciliationEnd]). This round will complete on [SystemReconciliationEnd], and [NextRoundName] will start on [NextRoundStart].
                    //   5) [Round name] of [GroupName] is currently allowing student reconciliatoin for [friendly duration] until [StudentReconciliationEnd]. This round will complete on [SystemReconciliationEnd], and [NextRoundName] will start on [NextRoundStart].
                    //   6) [Round name] of [GroupName] is being completed by program administrators. [NextRoundName] will start in [friendly duration] on [NextRoundStart].
                    //   7) [Round name] of [GroupName] completed on [RoundEnd], and bidding for [NextRoundName] will start in [friendly duration] on [NextRoundStart].
                    //
                    //   8) (same as #3)
                    //   9) Bidding has ended for [Round name] of [GroupName], and bids are currently being processed. You can reconcile your conflicts starting in [friendly duration] (from [StudentReconciliationStart] until [StudentReconciliationEnd]). This round will complete on [SystemReconciliationEnd], and your enrolllments should be visible thereafter.
                    //  10) [Round name] of [GroupName] is currently allowing student reconciliatoin for [friendly duration] until [StudentReconciliationEnd]. Your enrollments should be visible after [SystemRecEnd].
                    //  11) [Round name] of [GroupName] is being completed by program administrators. Your enrollments should be visible after [SystemRecEnd].
                    //  12) Bidding ended on [LastRoundSysRecEnd], and your enrollments should be posted soon.
                    // 
                    //  m1) You currently have [current points] remaining (of [starting points]). 
                    //  m2) You have [conflict count] conflicts that must be resolved.


                    // If there are no auctions or rounds, we're in state 1.
                    var stateOffset = 0;
                    var currentState = 1;
                    var now = this.utility.getCurrentDate();
                    var currentRound, previousRound, nextRound = {};
                    var currentAuction = auctions[0]; //get the first one - needs to change so we can loop through active auctions
                    if (currentAuction) {                       
                        var currentRoundIndex = currentAuction.Rounds.findIndex(r => (new Date(r.BiddingStart) <= now) && (new Date(r.SystemRecEnd) > now));
                        var previousRoundIndex = currentAuction.Rounds.findIndex(r => (new Date(r.SystemRecEnd) < now));
                        var nextRoundIndex = currentAuction.Rounds.findIndex(r => (new Date(r.BiddingStart) > now) && (new Date(r.SystemRecEnd) > now));
                        currentAuction.CurrentBidPoints = points ? points.CurrentBidPoints : 0;
                        currentAuction.StartingBidPoints = points ? points.StartingBidPoints : 0;
                        if (currentRoundIndex > -1) {
                            currentRound = currentAuction.Rounds[currentRoundIndex];
                        }
                        if (previousRoundIndex > -1)
                            previousRound = currentAuction.Rounds[previousRoundIndex];
                        if (nextRoundIndex > -1)
                            nextRound = currentAuction.Rounds[nextRoundIndex];
                        if ((currentRoundIndex < 0) && (previousRoundIndex < 0) && (nextRoundIndex > -1))
                            currentState = 2;
                        else if ((currentRoundIndex < 0) && (previousRoundIndex > -1) && (nextRoundIndex < 0))
                            currentState = 12;
                        else {
                            if (currentRoundIndex == currentAuction.Rounds.length - 1)
                                stateOffset = 5; //we are in the last round

                            if ((currentRoundIndex < 0) && (previousRoundIndex > -1) && (nextRoundIndex > -1))
                                currentState = 7; //between rounds
                            else if ((currentRoundIndex < 0) && (nextRoundIndex > -1))
                                currentState = 2 + stateOffset;
                            else if (currentRoundIndex > -1 && ( (now > new Date(currentRound['BiddingStart'])) && (now < new Date(currentRound['ProcessingStart']))))
                                currentState = 3 + stateOffset;
                            else if (currentRoundIndex > -1 && ( (now > new Date(currentRound['ProcessingStart'])) && (now < new Date(currentRound['StudentRecStart']))))
                                currentState = 4 + stateOffset;
                            else if (currentRoundIndex > -1 && ( (now > new Date(currentRound['StudentRecStart'])) && (now < new Date(currentRound['SystemRecStart']))))
                                currentState = 5 + stateOffset;
                            else if (currentRoundIndex > -1 && ( (now > new Date(currentRound['SystemRecStart'])) && (now < new Date(currentRound['SystemRecEnd']))))
                                currentState = 6 + stateOffset;
                        }
                    }
                    var whatToShow = [];
                    if ((currentState == 3) || (currentState == 8))
                        whatToShow.push('3');
                    else
                        whatToShow.push(currentState.toString());

                    if ((currentState == 2) || (currentState == 6) || (currentState == 7))
                        whatToShow.push('A');
                    else if ((currentState == 3) || (currentState == 8))
                        whatToShow.push('B');
                    else if ((currentState == 5) || (currentState == 10))
                        whatToShow.push('C');

                    if (currentState > 2 && currentState <= 8)
                        whatToShow.push('m1')
                    if (currentState == 5 || currentState == 10)
                        whatToShow.push('m2');
                    
                    return of({ currentAuction, currentRound, nextRound, previousRound, whatToShow })
                })
            );
        }

        return this._auctionInfo$;
    }

    public add(SectionId, RoundId, Points) {

        const bid = {
            SectionId,
            RoundId,
            Points
        };

        return this.http.post<any>(environment.apiUrl + '/bidding/self', bid, this.httpOptions).pipe(
            tap(_ => { this._bids$ = null; this._auctionBids$ = null; }),
            tap(() => this.enrollmentService.fetchEnrollmentHistory())
        ); // setting bid to null forces the bids to be refetched
    }

    public delete(SectionId, AuctionId, refresh) {

        var httpOptions = {
            headers: new HttpHeaders({
                'Content-Type': 'application/json',
            }),
            body: {
                AuctionId,
                SectionId
            }
        }

        return this.http.delete(environment.apiUrl + '/bidding/self', httpOptions).pipe(
            tap(_ => { if (refresh) { this._bids$ = null; this._auctionBids$ = null; } this._calendarBids$ = null; }),
            tap(() => this.enrollmentService.fetchEnrollmentHistory())
        ); //force the bids to be refetched
    }

    public updateBids(bidsToUpdate: BidUpdate[]) {
      
        return this.http.put<any>(environment.apiUrl + '/bidding/self', bidsToUpdate, this.httpOptions).pipe(
            tap(_ => { this._bids$ = null; this._auctionBids$ = null; }), // setting auctionbid to null forces the bids to be refetched
            tap(() => this.enrollmentService.fetchEnrollmentHistory())
        ); 
    }

    public updateBiddingCalendar(biddingCalendar: BiddingCalendar): Observable<BiddingCalendar> {
        var activeBidding = false;
        if (this._calendarBids$ == null) {
            biddingCalendar.defaultDate = undefined;
            this._calendarBids$ = this.bids$.pipe(
                map(result => {                    
                    if (result) {
                        activeBidding = result.Timeline.some(t => t.Value == "Place your bids" && t.CurrentState == true);
                        //empty the arrays
                        biddingCalendar.filterCurrent.splice(0, biddingCalendar.filterCurrent.length); 
                        biddingCalendar.filterLost.splice(0, biddingCalendar.filterLost.length);
                        biddingCalendar.filterWon.splice(0, biddingCalendar.filterWon.length);
                        biddingCalendar.filterConflicts.splice(0, biddingCalendar.filterConflicts.length);
                        biddingCalendar.calendarEventSource.splice(0, biddingCalendar.calendarEventSource.length);
                        var meetingNumber = 0;

                        for (let key in result.Bids) {
                            var startOfClass = undefined;
                            for (let meeting in result.Bids[key].Meetings) {

                                let thisMeeting = result.Bids[key].Meetings[meeting];
                                let itemType = thisMeeting.Days.length == 0 ? CalendarItemType.standard : CalendarItemType.recurring;
                                let location = null;

                                let startDate = this.setDateTime(result.Bids[key].ClassStart, thisMeeting.Start);
                                let endDate = this.setDateTime(result.Bids[key].ClassEnd, thisMeeting.End);
                                
                                if ((thisMeeting.Building.length > 0 && thisMeeting.Building != "Unknown") && thisMeeting.Room.length > 0)
                                    location = thisMeeting.Building + " " + thisMeeting.Room;
                                var meetingDays = thisMeeting.Days.split('').map(d => CalendarDaysOfWeek[d]);
                                var startDay = startDate.getDay();
                               
                                //This figures out the actual start of the class, not the start of the term.  Needed for the DefaultDate.
                                for (let d of meetingDays) {
                                    
                                    var daysToAdd = d - startDay;
                                    var firstD = new Date(startDate); //copy object
                                    firstD = new Date( firstD.setDate(firstD.getDate() + daysToAdd) );
                                                                        
                                    if (firstD < startDate) //increase by a week
                                        firstD = new Date(firstD.setDate(firstD.getDate() + 7));
                                    //  If the startDate is before the firstDay of this class and we haven't figured out the startOfClass or the startOfClass is later
                                    //  than the firstDay of this class, update the startOfClass.
                                    if (((startDate < firstD) && ((startOfClass == undefined) || (startOfClass > firstD))))
                                        startOfClass = firstD;                                                                      
                                }

                                var calendarItem = new CalendarItem(
                                    itemType,
                                    location != null ? result.Bids[key].ClassNumber + '\r\n' + location : result.Bids[key].ClassNumber,                                    
                                    startDate,
                                    endDate,                                    
                                    meetingDays);

                                calendarItem.url = '';

                                calendarItem.extendedProps = {
                                    id: meetingNumber
                                };

                                meetingNumber++;

                                if (result.Bids[key].Status == "Current Bid")
                                    biddingCalendar.filterCurrent.push(calendarItem);  
                                if (result.Bids[key].Status == "Won Bid") {
                                    if (result.Bids[key].Conflicts)
                                        biddingCalendar.filterConflicts.push(calendarItem)
                                    else
                                        biddingCalendar.filterWon.push(calendarItem)
                                }
                                if (result.Bids[key].Status == "Lost Bid")
                                    biddingCalendar.filterLost.push(calendarItem)
                            }

                            if ((biddingCalendar.defaultDate == undefined) || ((biddingCalendar.defaultDate != undefined) && (biddingCalendar.defaultDate > startOfClass)))
                                biddingCalendar.defaultDate = startOfClass;

                        }

                        if (biddingCalendar.defaultDate != undefined)
                            biddingCalendar.defaultDate = new Date(biddingCalendar.defaultDate).toISOString();
                      
                        biddingCalendar.calendarEventSource.push(biddingCalendar.calendarCurrent);                       
                        biddingCalendar.calendarEventSource.push(biddingCalendar.calendarWon);                        
                        biddingCalendar.calendarEventSource.push(biddingCalendar.calendarConflicts);                        
                        biddingCalendar.calendarEventSource.push(biddingCalendar.calendarLost);
                      
                    }     
                    return biddingCalendar;
                })
            );
        }

        return this._calendarBids$;
    }

    private setDateTime(datePart: any, timePart: any): Date {       
        let date = new Date(datePart);
        let time = new Date(timePart);
        date.setHours(time.getHours(), time.getMinutes());        
        return date;
    }

    public openBidDeleteConfirmModal(bid: IBid, actionType) {
        const modalRefConfirm = this.modalService.open(DisplayDetailsModalComponent);
        var start = new Date(bid.ClassStart).toLocaleDateString('en-US', { month: 'numeric', day: 'numeric', year: '2-digit' });
        var end = new Date(bid.ClassEnd).toLocaleDateString('en-US', { month: 'numeric', day: 'numeric', year: '2-digit' });

        modalRefConfirm.componentInstance.title = `<i class="fas fa-exclamation-circle text-red"></i> Confirm ${actionType} ${bid.Status}`;
        modalRefConfirm.componentInstance.body = `<p><strong>Are you sure you want to ${actionType.toLowerCase()} your bid for the following class? </strong></p>`;
        if(actionType == 'Cancel')
            modalRefConfirm.componentInstance.body += `<div class="alert alert-warning" role="alert">Your bid points will <strong>NOT</strong> be refunded</div>`;
        modalRefConfirm.componentInstance.body += `<ul>
            <li><strong>Class:</strong> ${bid.ClassName} </li>
            <li><strong>Number:</strong> ${bid.ClassNumber}</li>
            <li><strong>Starts:</strong> ${start}</li>
            <li><strong>Ends:</strong> ${end}</li>
            <li><strong>Points Bid:</strong> ${bid.Points}</li>
        </ul>
        <p><strong>This operation can not be undone.</strong></p>`;
        modalRefConfirm.componentInstance.useTwoButtons = true;
        modalRefConfirm.componentInstance.useDangerColor = true;

        return modalRefConfirm;
    }
       
    //public getCountDown$(auction): Observable<IInterval> {        
    //    var nextState = auction.Timeline.find(s => s.NextState);      
    //    if (nextState) {
    //        var endDate = new Date(nextState.Key);
    //        return this.countdownService.CountDown$(endDate);
    //    } else {
    //        var currentState = auction.Timeline.find(s => s.CurrentState);            
    //        if (currentState) {
    //            var endDate = new Date(auction.AuctionEnds);
    //            return this.countdownService.CountDown$(endDate);
    //        } else {
    //            var firstState = auction.Timeline[0];
    //            var endDate = new Date(firstState.Key);
    //            return this.countdownService.CountDown$(endDate);
    //        }            
    //    }
    //}

   

}

export interface IBidPoints {
    CurrentBidPoints: number;
    StartingBidPoints: number;
    Bids: IBid[];
    Timeline: any[];
    LosingBidAction: number;
}

export interface IBid {
    SectionId: number;
    Points: number;
    Status: string;
    ClassName: string;
    ClassNumber: string;
    ClassStart: Date;
    ClassEnd: Date;
    Meetings: any[];
    Conflicts: any[];
    isDeleted: boolean;
}

export class BidUpdate {
    AuctionId: number;
    SectionId: number;
    RoundId: number;
    Points: number;

    constructor(auctionId, sectionId, roundId, points) {
        this.AuctionId = auctionId;
        this.SectionId = sectionId;
        this.RoundId = roundId;
        this.Points = points;
    }
}


export class Auction {
    auctionId: number;
    bidding: boolean;
    processing: boolean;
    studentrec: boolean;
    sysrec: boolean;
    active: boolean;
    roundId: number;

    constructor(auctionId, bidding, processing, studentrec, sysrec, active, roundId) {
        this.auctionId = auctionId;
        this.bidding = bidding;
        this.processing = processing;
        this.studentrec = studentrec;
        this.sysrec = sysrec;
        this.active = active;
        this.roundId = roundId;       
    }
   
}

export class BiddingCalendar {

    defaultView: string;
    mobileView: string
    defaultHeader: any;
    mobileHeader: any;   
    mobileWindowSize: number;
    displayCalCurrent: boolean;
    displayCalWon: boolean;
    displayCalConflicts: boolean;
    displayCalLost: boolean;   
    showBidCalendar: boolean;
    customButtons: any;
    filterCurrent: any[] = [];
    filterWon: any[] = [];
    filterConflicts:any[] = [];
    filterLost: any[] = [];
    calendarEventSource: any[] = [];
    defaultDate: string;

    calendarCurrent: GroupedCalendarItems;
    calendarWon: GroupedCalendarItems;
    calendarConflicts: GroupedCalendarItems;
    calendarLost: GroupedCalendarItems;

    calendarEvent$ = new Subject<any>();

    private static instance: BiddingCalendar;

    private constructor() {
        this.defaultView = CalendarViews.timeGridWeek;        
        this.mobileView = CalendarViews.listMonth;
        this.defaultHeader = {
            left: [CalendarButtons.prev, CalendarButtons.today, CalendarButtons.next].join() + ' current,won,conflicts,lost',
            center: CalendarTitle.date,
            right: [CalendarViews.timeGridDay, CalendarViews.timeGridWeek, CalendarViews.dayGridMonth, CalendarViews.listWeek].join()
        };
        this.mobileHeader = {
            left: [CalendarButtons.prev, CalendarButtons.today, CalendarButtons.next].join() + ' current,won,conflicts,lost',
            center: CalendarTitle.date,
            right: [CalendarViews.timeGridDay, CalendarViews.listMonth].join()
        };
        
        this.mobileWindowSize = 1024;
        this.displayCalCurrent = true;
        this.displayCalWon = true;
        this.displayCalConflicts = true;
        this.displayCalLost = true;
        this.showBidCalendar = false;
        this.calendarCurrent = new GroupedCalendarItems(this.filterCurrent, '#3b7ca7', 'white', '#3b7ca7', 'current', 'currentGroup');
        this.calendarWon = new GroupedCalendarItems(this.filterWon, '#2CB54C', 'black', '#2CB54C', 'won', 'wonGroup');
        this.calendarConflicts = new GroupedCalendarItems(this.filterConflicts, '#ffc107', 'black', '#ffc107', 'conflicts', 'conflictsGroup');
        this.calendarLost = new GroupedCalendarItems(this.filterLost, '#19B4CC', 'black', '#19B4CC', 'lost', 'lostGroup');        
        this.customButtons = {
            current: {
                text: 'Current',
                click: () => this.calendarEventReload(event),
                active: this.displayCalCurrent
            },
            won: {
                text: 'Won',
                click: () => this.calendarEventReload(event),
                active: this.displayCalWon
            },
            conflicts: {
                text: 'Conflicts',
                click: () => this.calendarEventReload(event),
                active: this.displayCalConflicts
            },
            lost: {
                text: 'Lost',
                click: () => this.calendarEventReload(event),
                active: this.displayCalLost
            }
        }
    }

    //Singleton pattern
    static getInstance(): BiddingCalendar {
        if (!BiddingCalendar.instance) {
            BiddingCalendar.instance = new BiddingCalendar();
        }

        return BiddingCalendar.instance;
    }

    public calendarEventReload(event) {
        this.calendarEventSource = [];        
        event.target.classList.toggle('fc-button-active');
        //Switches the stored setting value and the custom button value
        if (event.target.innerText == "Current") {
            this.displayCalCurrent = !this.displayCalCurrent;
            this.customButtons.current.active = !this.customButtons.current.active;
        }
        if (event.target.innerText == "Won") {
            this.displayCalWon = !this.displayCalWon;
            this.customButtons.won.active = !this.customButtons.won.active;
        }

        if (event.target.innerText == "Conflicts") {
            this.displayCalConflicts = !this.displayCalConflicts;
            this.customButtons.conflicts.active = !this.customButtons.conflicts.active;
        }

        if (event.target.innerText == "Lost") {
            this.displayCalLost = !this.displayCalLost;
            this.customButtons.lost.active = !this.customButtons.lost.active;
        }
        //Displays selected data
        if (this.displayCalCurrent)
            this.calendarEventSource.push(this.calendarCurrent);

        if (this.displayCalWon)
            this.calendarEventSource.push(this.calendarWon);

        if (this.displayCalConflicts)
            this.calendarEventSource.push(this.calendarConflicts);

        if (this.displayCalLost)
            this.calendarEventSource.push(this.calendarLost);

        this.calendarEvent$.next(this.calendarEventSource);
    }
}

@Pipe({
    name: 'biddingStatus',
    standalone: false
})

export class BiddingStatusPipe implements PipeTransform {
    transform(timeline: any[]): string {
        var current = timeline.find(t => t.CurrentState == true)
        if (current)
            return ' left to ' + current.Value.toLowerCase() + '.';
        else
            return ' until Bidding starts!';      
    }

}
